import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Chip } from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { AgGridReact } from "ag-grid-react";
import { AddButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import DataState from "@plannel/components/aggrid/DataState";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import GridDatePicker from "@plannel/components/GridDatePicker";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import scenarioService from "@plannel/services/ai/scenarioService";
import { FONT } from "@plannel/components/ai/typographyTokens";
import LlmUsagePriceSuggestDialog from "./LlmUsagePriceSuggestDialog";
import { PRICE_AMT_FIELDS as AMT_FIELDS, PRICE_AMT_LABEL_KEYS as AMT_LABEL_KEYS, PRICE_AMT_WIDTHS, dateStr, localTs }
  from "./llmUsageUtil";

// 단가 마스터 — 초기값은 넣지 않는다(공식 가격표 확인 후 관리자가 등록). 금액은 항상 통화코드와 함께 저장.
// 행 추가·변경·삭제 표시는 표준 마스터 화면과 같은 공용 DataState 를 쓴다(신규=노랑, 변경=좌측 적색 띠).
// 통화 후보 — 회사정보 화면(CompanyProfile)의 목록과 같은 코드. 그 화면 지역 상수라 공유 불가라 여기에 둔다
const CURRENCY_CODES = ["USD", "KRW", "JPY", "EUR", "CNY", "TWD", "VND", "IDR"];

const LlmUsagePriceTab = ({ viewName, reloadKey, notify, onSaved, toolbarEl }) => {
  const { t } = useTranslation();
  const gridRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [models, setModels] = useState([]);
  // 제공자 후보는 서버(기록 어댑터와 같은 상수)에서 받는다 — 화면에 따로 적으면 이름이 갈린다
  const [providers, setProviders] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);

  const load = async () => {
    try {
      const [p, m] = await Promise.all([scenarioService.llmUsagePrices(), scenarioService.llmUsageModels()]);
      setRows(p?.prices || []);
      setModels(m?.models || []);
      setProviders(m?.providers || []);
    } catch {
      setRows([]); setModels([]); setProviders([]);
      notify("error", t("agentTraceLoadFail"));
    } finally {
      DataState.initialize(gridRef.current?.api);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  // id=null 이 신규 행 표식 — DataState 가 이걸로 created/updated 를 가른다
  const addRow = (preset = {}) => {
    const api = gridRef.current?.api;
    if (!api) return;
    const newRow = { id: null, provider_cd: providerOptions[0], model_id: "", currency_cd: "USD",
      effective_from: dateStr(new Date()), ver_num: 0, ...preset };
    DataState.setDataState(GridUtils.addRow(api, newRow));
  };

  // 단가 가져오기 창은 저장 후 이 표를 다시 읽는다 — 저장 안 한 편집이 있으면 사라지므로 먼저 막는다
  const openSuggest = () => {
    const api = gridRef.current?.api;
    if (api) api.stopEditing();
    if (api && DataState.getAllStateData(api).length > 0) {
      notify("warning", t("MSG_UnsavedChanges"));
      return;
    }
    setSuggestOpen(true);
  };

  const afterSuggestSaved = async () => {
    await load();
    onSaved?.();
  };

  const save = async () => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.stopEditing();
    const changed = DataState.getAllStateData(api);
    if (changed.length === 0) {
      notify("warning", t("MSG_NoSaveData"));
      return;
    }
    // 표시 전용 감사 열은 보내지 않는다
    const payload = changed.map(({ effective_to, rowIndex, created_ts, created_by, created_by_nm,
      updated_ts, updated_by, updated_by_nm, ...r }) => ({
      ...r, effective_from: dateStr(r.effective_from) || r.effective_from,
    }));
    try {
      await scenarioService.llmUsagePricesSave(payload);
      notify("success", t("llmUsagePriceSaved"));
      await load();
      onSaved?.();
    } catch (e) {
      const status = e?.response?.status;
      notify("error", status === 409 ? t("llmUsagePriceConflict") : t("llmUsagePriceInvalid"));
    }
  };

  const remove = async () => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.stopEditing();
    const deleteNodes = api.getSelectedNodes();
    if (deleteNodes.length === 0) {
      notify("warning", t("MSG_NoSelectedData"));
      return;
    }
    // 저장 전 신규 행은 서버에 없다 — 저장된 행만 논리 삭제 요청한다
    // (그리드에서 행을 빼는 것은 deleteState 가 끝에서 처리한다 — 여기서 또 remove 하면 노드를 못 찾아 에러)
    const ids = deleteNodes.filter((n) => n?.data?.id).map((n) => String(n.data.id));
    DataState.deleteState(api, deleteNodes);
    if (ids.length === 0) return;
    try {
      await scenarioService.llmUsagePricesDelete(ids);
      await load();
      onSaved?.();
    } catch {
      notify("error", t("agentTraceLoadFail"));
      await load();
    }
  };

  // 실적에 있는 제공자를 먼저, 그다음 서버가 준 후보 — 리치셀렉트는 목록 밖 값을 받지 않으니 빠짐이 없어야 한다
  const providerOptions = useMemo(() => [...new Set([
    ...models.map((m) => m.provider_cd),
    ...rows.map((r) => r.provider_cd),
    ...providers,
  ].filter(Boolean))], [models, rows, providers]);

  // 등록된 단가의 통화를 먼저, 그다음 기본 후보 — 리치셀렉트는 목록 밖 값을 받지 않는다
  const currencyOptions = useMemo(
    () => [...new Set([...rows.map((r) => r.currency_cd), ...CURRENCY_CODES].filter(Boolean))],
    [rows]);

  const columnDefs = useMemo(() => [
    { field: "provider_cd", headerName: t("llmUsageProvider"), width: 130,
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: { values: providerOptions, allowTyping: true, filterList: true, searchType: "matchAny" } },
    // 모델 ID 가 가장 길어 남는 폭을 이 열이 흡수한다 — 좁아지면 minWidth 로 고정되고 가로 스크롤
    { field: "model_id", headerName: t("agentTraceMetricModel"), minWidth: 300, flex: 1 },
    // 표시명 — 단가 가져오기가 가격표 이름을 채우고, 없거나 다르면 여기서 고친다(단가 결합 키는 모델 ID)
    { field: "model_nm", headerName: t("llmUsageModelName"), width: 180 },
    // 날짜 입력은 공용 GridDatePicker — 브라우저 기본 달력은 OS 언어를 따라가 화면 언어와 어긋난다
    { field: "effective_from", headerName: t("effStartDt"), width: 130,
      type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "day" } },
    { field: "effective_to", headerName: t("effEndDt"), width: 130, editable: false },
    { field: "currency_cd", headerName: t("currencyCd"), width: 110,
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: { values: currencyOptions, allowTyping: true, filterList: true, searchType: "matchAny" } },
    // 100만 토큰당 단가 4열은 그룹 헤더로 묶는다 — 열마다 같은 단위를 반복하지 않는다
    // 값은 숫자만 — 표준 숫자 에디터(음수·문자 차단). 빈 값은 "단가 미등록"이라 null 로 남긴다
    {
      headerName: t("llmUsagePricePerMtok"), marryChildren: true,
      children: AMT_FIELDS.map((f) => ({
        field: f, headerName: t(AMT_LABEL_KEYS[f]), width: PRICE_AMT_WIDTHS[f], type: ["rightAligned"],
        cellDataType: "number", cellEditor: "agNumberCellEditor", cellEditorParams: { min: 0 },
        valueParser: (p) => (p.newValue === "" || p.newValue == null ? null : Number(p.newValue)),
      })),
    },
    ...[["created_by", "createdBy", "created_ts", "createdTs"],
      ["updated_by", "updatedBy", "updated_ts", "updatedTs"]].flatMap(([by, byKey, ts, tsKey]) => [
      // 사용자 미매칭(기본값 0 등)은 ID 대신 공란 — 이름은 서버가 z_user 에서 붙인다
      { colId: by, headerName: t(byKey), width: 110, type: ["nonEditableColumn"],
        valueGetter: (p) => p.data?.[`${by}_nm`] || "" },
      { field: ts, headerName: t(tsKey), width: 160, type: ["nonEditableColumn"],
        valueFormatter: (p) => localTs(p.value) },
    ]),
  ], [t, providerOptions, currencyOptions]);

  // 행 상태(신규·변경) 표시와 저장 대상 수집은 DefaultGridSetting 의 onCellValueChanged → DataState 가 담당한다
  const gridSetting = useMemo(
    () => DefaultGridSetting({ title: "llmUsageTabPrice", viewName: `${viewName}_PRICE`, columnDefs }),
    [viewName, columnDefs]);

  const unpriced = models.filter((m) => m.priced_flg !== "Y");

  return (
    <>
      {/* 포털은 Box 의 children propTypes 에 걸리므로 Box 밖에 둔다 */}
      {toolbarEl ? createPortal(
        <>
          <Button size="small" variant="outlined" startIcon={<CloudDownloadIcon />}
            sx={{ fontSize: FONT.BODY, mr: 1 }} onClick={openSuggest}>
            {t("llmUsagePriceFetch")}
          </Button>
          <AddButton onClick={() => addRow()} />
          <RemoveButton onClick={remove} />
          <SaveButton onClick={save} />
        </>, toolbarEl) : null}
      {suggestOpen && (
        <LlmUsagePriceSuggestDialog open={suggestOpen} onClose={() => setSuggestOpen(false)}
          onSaved={afterSuggestSaved} notify={notify} />
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, height: "100%" }}>
        {unpriced.length > 0 && (
          <Alert severity="warning" sx={{ fontSize: FONT.BODY, py: 0 }}>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
              {t("llmUsageUnpriced")}:
              {unpriced.map((m) => (
                <Chip key={`${m.provider_cd}|${m.model_id}`} size="small" clickable
                  label={`${m.provider_cd} · ${m.model_id}`}
                  onClick={() => addRow({ provider_cd: m.provider_cd, model_id: m.model_id })} />
              ))}
            </Box>
          </Alert>
        )}
        <Box className="ag-theme-balham" sx={{ flex: 1, minHeight: 240 }}>
          {/* 표준 마스터 화면과 같이 체크박스로 고른 행만 삭제 대상 */}
          <AgGridReact ref={gridRef} rowData={rows} columnDefs={columnDefs} {...gridSetting}
            rowSelection={{ ...gridSetting.rowSelection, headerCheckbox: true, checkboxes: true }} />
        </Box>
      </Box>
    </>
  );
};

export default LlmUsagePriceTab;
